import * as Sentry from '@sentry/react';
import {
  shouldDisableReplay,
  shouldDisableTracing,
  REPLAY_DISABLED_PATHS,
  TRACING_DISABLED_PATHS
} from './sentryConfig';

let replayInstance = null;

// Function to handle route changes and control Session Replay (COMPLETELY SILENT)
export const handleRouteChange = (newPath) => {
  if (!replayInstance) return;

  const shouldDisable = shouldDisableReplay(newPath);

  if (shouldDisable) {
    replayInstance.stop();
  } else {
    replayInstance.start();
  }

  // Add breadcrumb for internal Sentry tracking (data only, no visible logs)
  Sentry.addBreadcrumb({
    category: 'session-replay',
    message: `Session Replay ${shouldDisable ? 'disabled' : 'enabled'} for route: ${newPath}`,
    level: 'info',
    data: {
      path: newPath,
      replayEnabled: !shouldDisable,
      isDynamicRoute: /\/\d+\//.test(newPath), // Detects numbers in route
      timestamp: new Date().toISOString()
    }
  });
};

// Set up route change listeners
export const setupRouteListeners = (history) => {
  const currentPath = window.location.pathname;

  // Variable to avoid duplicate logs
  let lastProcessedPath = '';

  // Get replay instance for manual control
  replayInstance = Sentry.getReplay();

  // Function to process route changes avoiding duplicates
  const processRouteChange = (newPath) => {
    if (lastProcessedPath === newPath) return; // Avoid duplicate processing
    lastProcessedPath = newPath;
    handleRouteChange(newPath);
  };

  // Control Session Replay based on initial route
  const shouldDisable = shouldDisableReplay(currentPath);
  lastProcessedPath = currentPath; // Mark as processed

  if (!shouldDisable) {
    replayInstance.start();
  }

  // Listen for route changes using React Router history
  if (history) {
    history.listen((location) => {
      processRouteChange(location.pathname);
    });
  }

  // Also listen for browser navigation changes
  window.addEventListener('popstate', () => {
    processRouteChange(window.location.pathname);
  });

  // Additional method: observe URL changes using interval (backup method)
  let urlCheckInterval;
  const startUrlWatcher = () => {
    urlCheckInterval = setInterval(() => {
      const currentUrl = window.location.pathname;
      if (currentUrl !== lastProcessedPath) {
        processRouteChange(currentUrl);
      }
    }, 500); // Check every 500ms
  };

  // Start URL watcher
  startUrlWatcher();

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (urlCheckInterval) {
      clearInterval(urlCheckInterval);
    }
  });

  // Add initialization breadcrumb (internal data only, no visible logs)
  Sentry.addBreadcrumb({
    category: 'session-replay',
    message: 'Session Replay monitor initialized',
    level: 'info',
    data: {
      initialPath: currentPath,
      replayEnabled: !shouldDisable,
      tracingEnabled: !shouldDisableTracing(currentPath),
      disabledReplayPaths: REPLAY_DISABLED_PATHS,
      disabledTracingPaths: TRACING_DISABLED_PATHS
    }
  });
};

// Function to manually disable Session Replay
export const disableReplay = () => {
  if (replayInstance) {
    replayInstance.stop();
  }
};

// Function to manually enable Session Replay
export const enableReplay = () => {
  if (replayInstance) {
    replayInstance.start();
  }
};

// Function to check current Session Replay status
export const getReplayStatus = () => {
  if (!replayInstance) return null;

  const currentPath = window.location.pathname;
  const isReplayDisabled = shouldDisableReplay(currentPath);
  const isTracingDisabled = shouldDisableTracing(currentPath);
  const replayId = replayInstance.getReplayId();

  return {
    path: currentPath,
    isReplayDisabled: isReplayDisabled,
    isTracingDisabled: isTracingDisabled,
    hasActiveReplay: Boolean(replayId),
    replayId: replayId,
    disabledReplayPaths: REPLAY_DISABLED_PATHS,
    disabledTracingPaths: TRACING_DISABLED_PATHS
  };
};

// Function to check if tracing is disabled for current route
export const isTracingDisabled = () => {
  const currentPath = window.location.pathname;
  return shouldDisableTracing(currentPath);
};

// Function to get tracing status for specific path
export const getTracingStatusForPath = (path) => ({
  path: path,
  isTracingDisabled: shouldDisableTracing(path),
  disabledTracingPaths: TRACING_DISABLED_PATHS
});
